import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useSectionBackground } from "@/lib/sectionBackground";
import { Menu, X, Newspaper, LogIn, UserPlus, Lock } from "lucide-react";
import SocialIcons from "@/components/SocialIcons";
import TimezonePicker from "@/components/TimezonePicker";
import LanguagePicker from "@/components/LanguagePicker";
import { useI18n } from "@/lib/I18nContext";

const ColorBar = () => (
  <div className="color-bar">
    {["red","orange","yellow","lime","green","mint","sky","blue","mauve","purple","pink","peach"].map((c) => (
      <div key={c} style={{ background: `var(--c-${c})` }} />
    ))}
  </div>
);

export default function PublicShell({ children, forcePublicNews = false }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [menuOpen, setMenuOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const navigate = useNavigate();
  const { imageUrl: bgImage } = useSectionBackground({ forcePublic: forcePublicNews });

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  return (
    <div className="grain min-h-screen bg-[#0A0A0A] text-white relative">
      {bgImage && (
        <div
          aria-hidden="true"
          data-testid="section-bg-image"
          className="fixed inset-0 z-0 bg-cover bg-center bg-no-repeat pointer-events-none"
          style={{ backgroundImage: `url("${bgImage}")` }}
        />
      )}
      {bgImage && (
        <div
          aria-hidden="true"
          data-testid="section-bg-overlay"
          className="fixed inset-0 z-0 pointer-events-none"
          style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.88) 60%, rgba(0,0,0,0.94) 100%)" }}
        />
      )}
      <ColorBar />
      <header className="glass sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 md:h-24 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3" data-testid="public-brand-link">
            <img src="/img/trophy.png" alt="La Polla Mundialista" className="w-14 h-14 md:w-16 md:h-16 object-contain" />
            <div className="leading-none">
              <div className="font-display text-2xl md:text-3xl uppercase tracking-tight">{t("nav.brand")}</div>
              <div className="text-[11px] md:text-xs text-zinc-400 uppercase tracking-widest font-mono-data">{t("nav.brandSub")}</div>
            </div>
          </Link>
          <div className="flex items-center gap-2 md:gap-3">
            <TimezonePicker />
            <LanguagePicker />
            <button
              onClick={() => setMenuOpen(true)}
              data-testid="public-hamburger-button"
              aria-label={t("common.menu")}
              className="px-3 py-3 md:px-4 md:py-3 border border-zinc-800 hover:border-[var(--c-lime)] transition-colors flex items-center gap-2"
            >
              <Menu size={22} />
              <span className="hidden md:inline text-sm uppercase tracking-widest font-mono-data">{t("common.menu")}</span>
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <>
          <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
            onClick={() => setMenuOpen(false)} data-testid="public-menu-backdrop" />
          <aside className="fixed top-0 right-0 z-50 h-full w-full sm:w-96 bg-[#0A0A0A] border-l border-zinc-800 shadow-2xl overflow-y-auto slide-up"
            data-testid="public-menu-drawer">
            <div className="px-6 py-5 border-b border-zinc-800 flex items-center justify-between">
              <div className="font-display text-xl uppercase">{t("common.menu")}</div>
              <button onClick={() => setMenuOpen(false)} data-testid="public-menu-close" aria-label={t("common.close")}>
                <X size={18} />
              </button>
            </div>
            <nav className="px-4 py-4 space-y-1">
              <Link to="/noticias" onClick={() => setMenuOpen(false)} data-testid="public-menu-noticias"
                className="flex items-center gap-3 px-3 py-3 text-sm uppercase tracking-wider text-zinc-100 hover:bg-zinc-900 border border-zinc-800">
                <Newspaper size={16} /> <span className="flex-1">{t("nav.news")}</span>
              </Link>
              <button onClick={() => { setMenuOpen(false); navigate("/login"); }} data-testid="public-menu-login"
                className="w-full flex items-center gap-3 px-3 py-3 text-sm uppercase tracking-wider text-zinc-100 hover:bg-zinc-900 border border-zinc-800">
                <LogIn size={16} /> <span className="flex-1 text-left">{t("common.signin")}</span>
              </button>
              <button onClick={() => { setMenuOpen(false); navigate("/register"); }} data-testid="public-menu-register"
                className="w-full flex items-center gap-3 px-3 py-3 text-sm uppercase tracking-wider text-zinc-100 hover:bg-zinc-900 border border-zinc-800">
                <UserPlus size={16} /> <span className="flex-1 text-left">{t("publicMenu.register")}</span>
              </button>
              <div className="pt-3 mt-3 border-t border-zinc-800">
                <div className="text-[10px] uppercase tracking-widest text-zinc-600 font-mono-data px-3 mb-2">{t("publicMenu.requiresLogin")}</div>
                {[
                  { key: "perfil", label: t("nav.profile") },
                  { key: "reglas", label: t("nav.rules") },
                  { key: "fase1",  label: t("nav.phase1") },
                  { key: "fase2",  label: t("nav.phase2") },
                  { key: "ranking", label: t("nav.ranking") },
                  { key: "todos",  label: t("nav.allPicks") },
                ].map((it) => (
                  <div key={it.key} className="flex items-center gap-3 px-3 py-2.5 text-xs uppercase tracking-wider text-zinc-600">
                    <Lock size={12} /> <span className="flex-1">{it.label}</span>
                  </div>
                ))}
              </div>
            </nav>
          </aside>
        </>
      )}

      <main className="relative z-10">{children}</main>

          <footer className="border-t border-zinc-900 mt-16 pb-12">
        <div className="max-w-7xl mx-auto px-4 py-10 space-y-8">
          <div className="text-center">
            <div className="text-sm uppercase tracking-widest text-white font-mono-data mb-3">{t("footer.follow")}</div>
            <div className="font-display text-2xl md:text-3xl uppercase mb-5">{t("footer.tagline")}</div>
            <SocialIcons settings={settings} testidPrefix="public-social" />
          </div>
          <div className="flex items-center justify-between text-xs uppercase tracking-wider text-zinc-500 flex-wrap gap-2 border-t border-zinc-900 pt-6">
            <span>{t("footer.copyright")}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
